import React from "react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useLocation } from "@tanstack/react-router";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar, CLIENT_PORTAL_NAV_ITEMS, SETTINGS_NAV_ITEMS } from "@/components/Sidebar/ClientAppSidebar";
import { useTokenExpirationCheck } from "@/hooks/useTokenExpirationCheck";

export function ClientDashboardLayout() {
  const { t } = useTranslation();
  const location = useLocation();

  useTokenExpirationCheck();

  const pathSegments = location.pathname.split("/").filter((segment) => segment.length > 0);
  const allNavItems = [...CLIENT_PORTAL_NAV_ITEMS, ...SETTINGS_NAV_ITEMS];

  const breadcrumbs = pathSegments.map((segment, index) => {
    const currentPath = `/${pathSegments.slice(0, index + 1).join("/")}`;
    const navItem = allNavItems.find((item) => item.url === currentPath);
    const title = navItem?.title ?? segment.charAt(0).toUpperCase() + segment.slice(1);
    return {
      href: currentPath,
      label: title,
      isLast: index === pathSegments.length - 1,
    };
  });

  return (
    <SidebarProvider>
      <AppSidebar className="border-r" />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                {breadcrumbs.length === 0 && location.pathname === "/" && (
                  <BreadcrumbItem>
                    <BreadcrumbPage>{t("Dashboard")}</BreadcrumbPage>
                  </BreadcrumbItem>
                )}
                {breadcrumbs.map((crumb) => (
                  <React.Fragment key={crumb.href}>
                    <BreadcrumbItem>
                      {crumb.isLast ? (
                        <BreadcrumbPage>{t(crumb.label)}</BreadcrumbPage>
                      ) : (
                        <BreadcrumbLink asChild>
                          <Link to={crumb.href}>{t(crumb.label)}</Link>
                        </BreadcrumbLink>
                      )}
                    </BreadcrumbItem>
                    {!crumb.isLast && <BreadcrumbSeparator />}
                  </React.Fragment>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </header>
        <main className="flex flex-1 flex-col gap-4 p-4 pt-0">
          <Outlet />
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
